import { useCallback, useEffect, useRef, useState } from 'react'
import { Logo } from '../ui/EmptyState.tsx'

// Вход на сайт CRM без пароля: ссылка открывает бота, бот показывает тот же код и спрашивает «Войти?».
// Страница опрашивает сервер — после подтверждения сервер ставит cookie сессии, и страница перезагружается.

interface Started {
  link: string
  code: string
  secret: string
  expiresAt: string
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; login: Started; opened: boolean }
  | { kind: 'denied' }
  | { kind: 'error'; text: string }

const POLL_MS = 2000

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`/api/v1/auth/web/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => null)) as T & { error?: { message?: string } }
  if (!res.ok) throw new Error(data?.error?.message || 'Не удалось связаться с сервером — попробуйте ещё раз')
  return data
}

export function WebLogin() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const pollRef = useRef<number | null>(null)

  const start = useCallback(async () => {
    setPhase({ kind: 'loading' })
    try {
      const login = await post<Started>('start', {})
      setPhase({ kind: 'ready', login, opened: false })
    } catch (e) {
      setPhase({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка' })
    }
  }, [])

  // Ссылку готовим сразу — тогда кнопка открывает Telegram без задержки (и без блокировки всплывающих окон)
  useEffect(() => {
    void start()
  }, [start])

  // Опрос: подтвердили в боте — перезагружаем страницу (cookie уже стоит)
  useEffect(() => {
    if (phase.kind !== 'ready') return
    const { secret, expiresAt } = phase.login
    const tick = async () => {
      if (document.visibilityState === 'hidden') return
      if (Date.parse(expiresAt) < Date.now()) {
        void start()
        return
      }
      try {
        const r = await post<{ status: string }>('poll', { secret })
        if (r.status === 'approved') window.location.reload()
        else if (r.status === 'denied') setPhase({ kind: 'denied' })
        else if (r.status === 'expired') void start()
      } catch {
        // временная ошибка сети — попробуем на следующем шаге
      }
    }
    pollRef.current = window.setInterval(() => void tick(), POLL_MS)
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current)
    }
  }, [phase, start])

  return (
    <div className="fatal">
      <div className="empty web-login">
        <div className="empty__logo">
          <Logo size={56} />
        </div>
        <div className="empty__title">OVO CRM</div>
        <div className="empty__text">
          Задачи, команда и отчёты — на большом экране. Вход через Telegram, без пароля.
        </div>
        {phase.kind === 'loading' && <span className="spinner" role="status" aria-label="Готовим вход" />}
        {phase.kind === 'error' && (
          <>
            <div className="hint hint--attn">{phase.text}</div>
            <button type="button" className="btn btn--primary btn--block" onClick={() => void start()}>
              Попробовать снова
            </button>
          </>
        )}
        {phase.kind === 'denied' && (
          <>
            <div className="hint hint--attn">Вход отклонён в боте.</div>
            <button type="button" className="btn btn--primary btn--block" onClick={() => void start()}>
              Войти заново
            </button>
          </>
        )}
        {phase.kind === 'ready' && (
          <>
            <a
              className="btn btn--primary btn--block"
              href={phase.login.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setPhase({ ...phase, opened: true })}
            >
              Войти через Telegram
            </a>
            <div className="web-login__code">
              Код для проверки: <b>{phase.login.code}</b>
              <div className="hint">Бот покажет такой же код — нажмите в нём «Войти».</div>
            </div>
            {phase.opened && (
              <div className="web-login__wait" role="status">
                <span className="spinner" aria-hidden="true" /> Ждём подтверждения в Telegram…
              </div>
            )}
            <div className="hint web-login__alt">
              Нет Telegram на этом компьютере? Откройте ссылку на телефоне:
              <br />
              <span className="web-login__link">{phase.login.link}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
